import * as Yup from 'yup';
import { requiredErrorMessage } from 'constants/constants';
import {
  DistanceBasedTravel,
  SpendBasedTravel,
  HotelStay,
} from 'models/ActivityDataEntryScopes/businessTravel.model';
import { maxFourDecimalsTest } from 'views/ActivityDataEntry/ScopeOneViewComponent/validationSchema';

// Helper function to check if any field in distance travel is filled
const isDistanceFieldFilled = (fields: Partial<DistanceBasedTravel>): boolean =>
  !!fields.travelMode || !!fields.origin || !!fields.destination;

// Helper function to check if any field in spend travel is filled
const isSpendFieldFilled = (fields: Partial<SpendBasedTravel>): boolean =>
  !!fields.spendType || !!fields.money || !!fields.spendLocation;

// Helper function to check if any field in hotel stay is filled
const isHotelFieldFilled = (fields: Partial<HotelStay>): boolean =>
  !!fields.location || !!fields.hotelNights;

// Validation for distance-based travel entries
const distanceBasedTravelSchema = Yup.object().shape({
  travelMode: Yup.string()
    .nullable()
    .test('travelModeRequired', requiredErrorMessage, function (value) {
      const parentFields = this.parent as DistanceBasedTravel;
      if (isDistanceFieldFilled(parentFields) && !value) {
        return false;
      }
      return true;
    }),
  origin: Yup.string()
    .nullable()
    .test('originRequired', requiredErrorMessage, function (value) {
      const parentFields = this.parent as DistanceBasedTravel;
      if (isDistanceFieldFilled(parentFields) && !value) {
        return false;
      }
      return true;
    }),
  destination: Yup.string()
    .nullable()
    .test('destinationRequired', requiredErrorMessage, function (value) {
      const parentFields = this.parent as DistanceBasedTravel;
      if (isDistanceFieldFilled(parentFields) && !value) {
        return false;
      }
      return true;
    }),
  roundTrip: Yup.boolean().nullable().default(false),
  numberOfPassengers: Yup.number()
    .nullable()
    .default(1)
    .transform((value, originalValue) =>
      originalValue === '' ? 1 : (value ?? 1)
    )
    .test(
      'positivePassengers',
      'Number of passengers must be at least 1',
      value => value == null || Number(value) >= 1
    )
    .test(
      'integerPassengers',
      'Number of passengers must be a whole number',
      value => value == null || Number.isInteger(Number(value))
    ),
  carSize: Yup.string()
    .nullable()
    .test('carSizeNullForNonCar', 'car_size must be null when travel_mode is not car', function (value) {
      const { travelMode } = this.parent as DistanceBasedTravel;
      if (travelMode !== 'car' && value !== null && value !== undefined) {
        return false;
      }
      return true;
    }),
  carType: Yup.string()
    .nullable()
    .test('carTypeNullForNonCar', 'car_type must be null when travel_mode is not car', function (value) {
      const { travelMode } = this.parent as DistanceBasedTravel;
      if (travelMode !== 'car' && value !== null && value !== undefined) {
        return false;
      }
      return true;
    }),
  flightClass: Yup.string().nullable(),
});

// Validation for spend-based travel entries
const spendBasedTravelSchema = Yup.object().shape({
  spendType: Yup.string()
    .nullable()
    .test('spendTypeRequired', requiredErrorMessage, function (value) {
      const parentFields = this.parent as SpendBasedTravel;
      if (isSpendFieldFilled(parentFields) && !value) {
        return false;
      }
      return true;
    }),
  money: maxFourDecimalsTest
    .test('moneyRequired', requiredErrorMessage, function (value) {
      const parentFields = this.parent as SpendBasedTravel;
      if (isSpendFieldFilled(parentFields) && !value) {
        return false;
      }
      return true;
    })
    .test(
      'positiveMoney',
      'Amount must be greater than 0',
      value => value == null || Number(value) > 0
    ),
  moneyUnit: Yup.string()
    .nullable()
    .test('moneyUnitRequired', requiredErrorMessage, function (value) {
      const parentFields = this.parent as SpendBasedTravel;
      if (isSpendFieldFilled(parentFields) && !value) {
        return false;
      }
      return true;
    }),
  spendLocation: Yup.string()
    .nullable()
    .test('spendLocationRequired', requiredErrorMessage, function (value) {
      const parentFields = this.parent as SpendBasedTravel;
      if (isSpendFieldFilled(parentFields) && !value) {
        return false;
      }
      return true;
    }),
});

// Validation for hotel stay entries
const hotelStaySchema = Yup.object().shape({
  hotelNights: Yup.number()
    .nullable()
    .transform((value, originalValue) =>
      originalValue === '' ? null : value
    )
    .test('hotelNightsRequired', requiredErrorMessage, function (value) {
      const parentFields = this.parent as HotelStay;
      if (isHotelFieldFilled(parentFields) && !value) {
        return false;
      }
      return true;
    })
    .test(
      'positiveNights',
      'Number of nights must be at least 1',
      value => value == null || Number(value) >= 1
    )
    .test(
      'integerNights',
      'Number of nights must be a whole number',
      value => value == null || Number.isInteger(Number(value))
    ),
  location: Yup.string()
    .nullable()
    .test('locationRequired', requiredErrorMessage, function (value) {
      const parentFields = this.parent as HotelStay;
      if (isHotelFieldFilled(parentFields) && !value) {
        return false;
      }
      return true;
    }),
});

// Main validation schema for Business Travel (Category 6)
export const businessTravelValidationSchema = () => {
  return Yup.object().shape({
    distanceBasedTravel: Yup.array().of(distanceBasedTravelSchema),
    spendBasedTravel: Yup.array().of(spendBasedTravelSchema),
    hotelStays: Yup.array().of(hotelStaySchema),
  });
};
